import * as THREE from 'three';
import type { BlockPhysics } from '../world/collision';
import type { NavGrid, NavPoint } from '../world/navgrid';
import type { EnemyArchetype } from './archetypes';
import { createHealth, applyDamage, tickHealth, FLASH_DURATION, type Health } from '../combat/damage';
import type { AimTarget } from '../combat/autoaim';
import { VERTICAL_ENGAGEMENT_RANGE } from '../combat/spatial';
import { PLAYER_TOTAL_HEIGHT } from '../world/controller';
import { CHARACTER_FORWARD_YAW_OFFSET } from '../core/assets';

export type EnemyState = 'idle' | 'chase' | 'telegraph' | 'attack' | 'dive' | 'recover' | 'burrow' | 'submerged' | 'emerge' | 'leash' | 'dead';

const ATTACK_ACTIVE_WINDOW = 0.15;
const DEATH_FADE_DURATION = 0.5;
const PRIMITIVE_PULSE_SCALE = 1.25;
const GROUND_TOLERANCE = 0.6; // generous — ground enemies don't jump/vault, just need to sit on whatever floor region is under them
const WHITE = new THREE.Color(0xffffff);
// A melee hit lands only if the player is STILL within this generous
// grace margin over the archetype's raw attackRange at the exact moment
// the attack's active frame fires (not just when the telegraph started) —
// §0.7's fire-time-capture law applied to melee. Without this, a player
// who retreats/slides away during the telegraph still eats the hit, which
// a 3-lens review confirmed makes melee telegraphs functionally
// undodgeable by movement (only the ranged lobber, a real traveling
// projectile, was already immune to this).
const MELEE_HIT_GRACE = 1.25;
const LOS_CHECK_HEIGHT = 1; // chest height — matches TheNine's own MUZZLE_HEIGHT convention (combat/weapon.ts)
const REPATH_INTERVAL = 0.6; // seconds between nav-path requests while chasing — A* isn't cheap enough to run every tick per enemy
const PATH_WAYPOINT_ARRIVE_RADIUS = 0.6;
const LEASH_ARRIVE_RADIUS = 1;
const RETREAT_DISTANCE = 4; // how far a kiting ranged enemy backs away when the player closes inside its preferred min range
const FLYER_HOVER_SPEED = 0.8; // m/s — the small idle wobble/circle speed, unrelated to moveSpeed (its real chase speed)
const FLYER_HOVER_PHASE_SPEED = 2.2;
const ALTITUDE_LERP_RATE = 3; // 1/s-ish — how fast a flyer/diver's Y eases toward its target altitude
// S11 review fix — wanderIdle tuning (golden_pigeon only, archetype.wanderRadius>0).
const WANDER_SPEED = 1.1; // m/s — slower than every archetype's own moveSpeed (its would-be chase speed), a lazy drift, not a chase
const WANDER_REPICK_INTERVAL = 5; // seconds — baseline time between picking a new drift point
const WANDER_REPICK_JITTER = 2; // seconds — added on top of the interval so multiple wanderers (a future 2nd one) don't repick in lockstep
const WANDER_ARRIVE_RADIUS = 0.5; // close enough to the current drift point to pick a new one early, rather than waiting out the full timer

export interface EnemyVisual {
  group: THREE.Group;
  materials: THREE.Material[];
  mixer: THREE.AnimationMixer | null;
  clips: Map<string, THREE.AnimationClip> | null;
  baseScale: number;
}

export interface EnemyTickResult {
  /** Fully dead and faded out — the manager should despawn it and start its respawn timer. */
  removed: boolean;
  /** This tick is the melee attack's active frame — the manager should apply contact damage to the player. */
  meleeAttackFired: boolean;
  /** This tick is a ranged attack's release frame — the manager should spawn a lob projectile. */
  projectileToFire: { origin: THREE.Vector3; targetPos: THREE.Vector3 } | null;
  /**
   * C08/Wave M — 'burrower' movementMode only: this tick is the exact
   * instant `special_burrow` finishes and the enemy goes hidden/submerged —
   * the captured resurface point (§0.7 fire-time-capture), for a future
   * manager to spawn the warning-ripple VFX the C08 brief describes as
   * code-owned ("position capture and the warning ripple VFX are code — the
   * clips are the dive and the burst"). Null on every other tick and for
   * every non-burrower archetype.
   */
  submergeAt: THREE.Vector3 | null;
}

/**
 * One enemy instance, parameterized by an `EnemyArchetype` (Classic's own
 * registry-driven pattern — `BOSSES`, `PALETTES` — carried into 3D). Owns
 * its own state machine, health, and visual (GLB-animated or primitive).
 *
 * Four movement modes share this one class rather than four separate
 * classes, because they share almost everything else (health/knockback/
 * flash/death-fade/the telegraph-attack resolution two of the four modes
 * use unchanged; leashing applies to three of the four — see 'burrower'
 * below):
 *  - 'ground' resolves movement against `BlockPhysics.resolveHorizontal`
 *    PLUS `NavGrid` pathing while chasing (S03: replaces S02's direct-seek,
 *    which could get an enemy stuck on a corner it couldn't path around).
 *  - 'flyer' holds a fixed altitude above its home ground height, ignoring
 *    floors entirely, but still bumps into walls (buildings) via the same
 *    `resolveHorizontal` call at a thin vertical band around its own
 *    current altitude.
 *  - 'diver' is a genuinely different state machine (`updateDiver`):
 *    idle-hover -> telegraph -> a committed high-speed dive -> a vulnerable
 *    recover climb, instead of the shared telegraph/attack resolution.
 *  - 'burrower' (C08/Wave M) is also its own state machine
 *    (`updateBurrower`): telegraph -> burrow/hidden -> a submerged hold ->
 *    emerge, with a single hit check at emerge's own final frame. It never
 *    chases and is excluded from leashing entirely (see the leash-gate
 *    comment in `update()`).
 */
export class Enemy {
  readonly archetype: EnemyArchetype;
  readonly position = new THREE.Vector3();
  readonly health: Health;
  readonly group: THREE.Group;
  readonly spawnIndex: number;
  state: EnemyState = 'idle';

  private readonly visual: EnemyVisual;
  // Captured once at construction time — flash tint LERPS toward white from
  // each material's own original emissive rather than overwriting it, so an
  // asset with a deliberate baked-in glow (§4's "≤1 emissive material per
  // asset" allowance) doesn't get its glow silently erased every frame
  // flashT is 0.
  private readonly baseEmissive = new Map<THREE.MeshStandardMaterial, THREE.Color>();
  private stateT = 0;
  private attackCd = 0;
  private hitStunT = 0;
  private deathT = 0;
  private currentClip = '';
  private facing = 0;

  // Habitat home — captured at spawn/respawn, NOT re-derived from the
  // enemy's current position (which drifts while it chases/dives/hovers).
  // Leashing and a flyer's idle-altitude baseline both key off this.
  private homeX: number;
  private homeZ: number;
  // The real ground/floor height under `homeX,homeZ` (street 0, or the
  // rooftop's 16) — a per-INSTANCE value, not per-archetype, since the same
  // 'pigeon' archetype spawns in both a street-level and a rooftop habitat
  // (ai/habitats.ts's `groundHint`). A flyer/diver's absolute hover altitude
  // is always `homeGroundY + archetype.flightHeight`.
  private homeGroundY: number;

  // 'ground'-only nav-path state.
  private path: NavPoint[] = [];
  private pathIndex = 0;
  private repathTimer = 0;

  // 'flyer'/'diver'-only hover wobble phase — offset per spawn slot (below)
  // so multiple flyers don't circle in visible lockstep.
  private hoverPhase: number;
  // 'diver'-only — guards a dive from landing more than one hit (it
  // transitions to 'recover' the instant it does), and from firing a second
  // `meleeAttackFired` on a later tick of the same dive. C08/Wave M reuses
  // this field for 'burrower' too (guards the emerge burst the identical
  // way) — the two movement modes are mutually exclusive per instance, so
  // sharing one field costs nothing and avoids a near-duplicate.
  private hasHitThisDive = false;
  // 'diver'-only — the flight target captured ONCE when a telegraph
  // resolves into a dive (§0.7 fire-time-capture); the dive flies toward
  // this fixed point, never re-deriving it from the player's live position.
  // C08/Wave M reuses this field for 'burrower' too (the captured resurface
  // point) — same "mutually exclusive per instance, share the field"
  // reasoning as hasHitThisDive above.
  private readonly diveTarget = new THREE.Vector3();
  // S11 review fix — 'wanderIdle'-only state (archetype.wanderRadius>0).
  // Plain numbers, not a Vector3, matching homeX/homeZ's own convention —
  // only ever read/written together as an XZ pair, never passed to a
  // THREE.js API that wants a real Vector3.
  private wanderTargetX = 0;
  private wanderTargetZ = 0;
  private wanderRepickT = 0;
  // S08 memory audit: `update()` ran a fresh `new THREE.Vector3()` (plus a
  // second one on the rare same-position fallback) EVERY tick, for EVERY
  // enemy, unconditionally — at §3's ≤24-enemy ceiling that's up to 1440+
  // allocations/sec from this one line alone. Both the "toward player" and
  // "same-position fallback" cases now reuse this single scratch vector
  // (`.set()` for the fallback instead of allocating a new one) — it's read
  // synchronously within the same tick (`this.facing`, `updateChaseOrIdle`'s
  // `dirToPlayer` param) and never stored past that call.
  private readonly scratchDir = new THREE.Vector3();

  constructor(archetype: EnemyArchetype, visual: EnemyVisual, spawnX: number, spawnZ: number, spawnIndex: number, homeGroundY = 0) {
    this.archetype = archetype;
    this.visual = visual;
    this.group = visual.group;
    this.health = createHealth(archetype.maxHp, archetype.knockbackResist);
    this.spawnIndex = spawnIndex;
    this.homeX = spawnX;
    this.homeZ = spawnZ;
    this.homeGroundY = homeGroundY;
    this.hoverPhase = spawnIndex * 1.7;
    // S11 review fix: seed the wander target AT home so a freshly-spawned
    // wanderer's first frame doesn't drift toward (0,0) (this field's own
    // default) before its first real repick — `wanderRepickT=0` forces that
    // repick to happen on the very first `wanderIdle()` call regardless.
    this.wanderTargetX = spawnX;
    this.wanderTargetZ = spawnZ;
    const startY = archetype.movementMode === 'ground' ? homeGroundY : homeGroundY + archetype.flightHeight;
    this.position.set(spawnX, startY, spawnZ);
    for (const m of visual.materials) {
      if (m instanceof THREE.MeshStandardMaterial) this.baseEmissive.set(m, m.emissive.clone());
    }
  }

  // S08 spec-fidelity review finding (MEDIUM): allocated a fresh wrapper
  // object on every access — read once per LIVE enemy every tick via
  // `enemyManager.getEnemies().map((e) => e.aimTarget)`, up to §3's 24-enemy
  // ceiling. `position` was always just a REFERENCE to `this.position` (the
  // enemy's own live vector, never cloned), so the only thing that actually
  // needs to change per access is `isTargetable` — reusing one instance
  // field and updating that single field in place is behavior-identical
  // (every consumer already read `.position` live/synchronously within the
  // same tick, same as before) and drops this to zero allocations.
  private readonly cachedAimTarget: AimTarget = { position: this.position, isTargetable: false };

  get aimTarget(): AimTarget {
    // 'submerged' (C08/Wave M's burrower) is invisible (group.visible=false,
    // see updateBurrower) — excluded the same way 'dead' is, so auto-aim
    // can't lock onto something the player can't see. A no-op check for
    // every other archetype, since nothing else ever reaches this state.
    this.cachedAimTarget.isTargetable = this.state !== 'dead' && this.state !== 'submerged';
    return this.cachedAimTarget;
  }

  /**
   * Pooled reuse (S03 — §3's "Simultaneous enemies... pool everything"):
   * resets this SAME instance (same GLB clone, materials, mixer — none of
   * that is rebuilt) back to a fresh spawn at a new habitat point, instead
   * of `ai/spawner.ts` constructing a brand-new `Enemy` + `instantiate()`
   * every respawn cycle. That old per-respawn reallocation also never
   * called `.dispose()` on the discarded materials/geometries — a real,
   * if slow, GPU-resource leak this pooling incidentally closes too.
   */
  respawn(x: number, z: number, homeGroundY = 0): void {
    this.health.hp = this.health.maxHp;
    this.health.invulnerableT = 0;
    this.health.flashT = 0;
    this.health.knockbackVel.set(0, 0, 0);
    this.state = 'idle';
    this.stateT = 0;
    this.attackCd = 0;
    this.hitStunT = 0;
    this.deathT = 0;
    this.currentClip = '';
    this.path = [];
    this.pathIndex = 0;
    this.repathTimer = 0;
    this.hasHitThisDive = false;
    this.homeX = x;
    this.homeZ = z;
    this.homeGroundY = homeGroundY;
    // S11 review fix: same "reset to the new home, force an immediate
    // repick" reasoning as the constructor above — a respawned wanderer
    // must not drift toward its OLD home/target for one stale frame.
    this.wanderTargetX = x;
    this.wanderTargetZ = z;
    this.wanderRepickT = 0;
    const startY = this.archetype.movementMode === 'ground' ? homeGroundY : homeGroundY + this.archetype.flightHeight;
    this.position.set(x, startY, z);
    this.group.scale.setScalar(this.visual.baseScale);
    this.group.visible = true;
    this.playClip('idle', true);
  }

  private playClip(name: string, loop: boolean): void {
    if (!this.visual.mixer || !this.visual.clips) return;
    if (this.currentClip === name) return;
    const clip = this.visual.clips.get(name);
    if (!clip) return;
    this.visual.mixer.stopAllAction();
    const action = this.visual.mixer.clipAction(clip);
    action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    action.clampWhenFinished = !loop;
    action.reset().play();
    this.currentClip = name;
  }

  /** Returns true if this hit was lethal. Routes through the shared damage choke point (§0.7) — never mutates `.health.hp` itself. */
  takeDamage(amount: number, knockbackDir: THREE.Vector3, knockbackForce: number): boolean {
    // 'submerged' is untargetable (see aimTarget above) AND undamageable —
    // out of reach underground, same "invisible = can't be hit" reasoning.
    // A no-op check for every non-burrower archetype.
    if (this.state === 'dead' || this.state === 'submerged') return false;
    const died = applyDamage(this.health, amount, { knockbackDir, knockbackForce });
    if (died) {
      this.state = 'dead';
      this.deathT = DEATH_FADE_DURATION;
      this.currentClip = '';
      this.playClip('death', false);
    } else {
      this.hitStunT = 0.15;
      this.currentClip = '';
      this.playClip('hit', false);
    }
    return died;
  }

  // S08: enemy-side half of the reduced-motion/flash toggle S07 is wiring
  // for the PLAYER's own hit-flash (main.ts, gated on save.settings.
  // flashEnabled). S07 explicitly left the enemy side out of scope (ai/ is
  // outside its own territory) — defaults to true (identical to every
  // pre-S08 build) until EnemyManager.setFlashEnabled() is actually called,
  // which main.ts will do once save.settings exists to read from.
  update(dt: number, physics: BlockPhysics, playerPos: THREE.Vector3, separation: THREE.Vector3, nav: NavGrid | null, flashEnabled = true): EnemyTickResult {
    const result: EnemyTickResult = { removed: false, meleeAttackFired: false, projectileToFire: null, submergeAt: null };
    tickHealth(this.health, dt);
    this.applyKnockbackMovement(dt, physics);

    if (this.state === 'dead') {
      this.deathT -= dt;
      const t = Math.max(0, this.deathT / DEATH_FADE_DURATION);
      this.group.scale.setScalar(this.visual.baseScale * t);
      this.visual.mixer?.update(dt);
      this.syncTransform();
      result.removed = this.deathT <= 0;
      return result;
    }

    if (this.hitStunT > 0) this.hitStunT -= dt;
    if (this.attackCd > 0) this.attackCd -= dt;
    this.visual.mixer?.update(dt);

    // A target on a different floor level (e.g. the player on the fire-
    // escape ramp/rooftop directly above a ground-level enemy) is not
    // reachable no matter how close it reads horizontally — collapse
    // distance to "out of range" rather than letting aggro/attack fire
    // through a floor. Every movement mode shares this gate: flyer/diver
    // hover heights (1.2-1.6m, or 16m+ for the rooftop habitat) are chosen
    // specifically so a street-level flyer only ever engages a street-level
    // player, and a rooftop flyer only a player who's actually climbed up.
    const outOfVerticalRange = Math.abs(playerPos.y - this.position.y) > VERTICAL_ENGAGEMENT_RANGE;
    const toPlayer = this.scratchDir.subVectors(playerPos, this.position).setY(0);
    const horizDistance = toPlayer.length();
    const distance = outOfVerticalRange ? Infinity : horizDistance;
    const dirToPlayer = !outOfVerticalRange && horizDistance > 1e-4 ? toPlayer.divideScalar(horizDistance) : toPlayer.set(0, 0, 1);
    this.facing = Math.atan2(dirToPlayer.x, dirToPlayer.z);

    // Leashing: once actively doing something (not idle, not already
    // leashing) beyond leashRange from HOME (not the player), give up and
    // head back — checked before any mode-specific dispatch so it applies
    // uniformly regardless of movement mode.
    // C08/Wave M: 'burrower' never leashes — it doesn't chase in the normal
    // sense at all (it waits, then teleports to a captured point on
    // emerge), so "too far from home, give up and walk back" doesn't apply;
    // worse, the captured emerge point can legitimately land outside
    // leashRange (aggroRange can exceed leashRange today, e.g. plain rat's
    // own 14 vs 12), which would otherwise yank a burrower into 'leash'
    // mid-sequence the instant it resurfaces. A no-op condition for every
    // other movementMode.
    const distFromHome = Math.hypot(this.position.x - this.homeX, this.position.z - this.homeZ);
    if (this.archetype.movementMode !== 'burrower' && this.state !== 'leash' && this.state !== 'idle' && distFromHome > this.archetype.leashRange) {
      this.state = 'leash';
      this.stateT = 0;
      this.path = [];
    }

    if (this.state === 'leash') {
      this.updateLeash(dt, physics, nav, separation);
    } else if (this.archetype.movementMode === 'diver') {
      this.updateDiver(dt, physics, playerPos, distance, outOfVerticalRange, separation, result);
    } else if (this.archetype.movementMode === 'burrower') {
      this.updateBurrower(dt, playerPos, distance, outOfVerticalRange, result);
    } else if (this.state === 'telegraph' || this.state === 'attack') {
      this.updateAttackResolution(dt, distance, playerPos, physics, result);
    } else {
      this.updateChaseOrIdle(dt, physics, nav, dirToPlayer, distance, separation, playerPos);
    }

    this.applyPrimitivePulse();
    this.applyFlashTint(flashEnabled);
    this.syncTransform();
    return result;
  }

  /**
   * The shared telegraph->attack resolution for 'ground' and 'flyer'
   * archetypes — the S02 version, plus one S03 review fix: a RANGED
   * archetype re-checks line-of-sight at the moment the telegraph resolves,
   * not just at the moment it opened. Without this, a player who stepped
   * behind cover mid-telegraph (0.4s for the trash-can lobber) still ate a
   * lob fired blind through the wall that broke sight — the lob projectile
   * itself has no wall collision at all (combat/projectiles.ts), so nothing
   * downstream would have caught it either. A broken sightline at resolve
   * time cancels into a normal cooldown (`chase`, not `attack`) rather than
   * firing — the shot never leaves the barrel, matching the archetype's own
   * documented intent ("won't lob a can through a building wall").
   */
  private updateAttackResolution(dt: number, distance: number, playerPos: THREE.Vector3, physics: BlockPhysics, result: EnemyTickResult): void {
    this.stateT -= dt;
    if (this.state === 'telegraph' && this.stateT <= 0) {
      if (this.archetype.ranged && !this.hasLineOfSightIfRequired(physics, playerPos)) {
        this.state = 'chase';
        this.attackCd = this.archetype.attackCooldown;
        return;
      }
      this.state = 'attack';
      this.stateT = ATTACK_ACTIVE_WINDOW;
      this.playClip('attack1', false);
      if (this.archetype.ranged) {
        result.projectileToFire = { origin: this.position.clone(), targetPos: playerPos.clone() };
      } else if (distance <= this.archetype.attackRange * MELEE_HIT_GRACE) {
        // Captured HERE, at the attack's own active frame — re-measured
        // against the player's LIVE position, not the position at the
        // moment the telegraph started. §0.7's fire-time-capture law.
        result.meleeAttackFired = true;
      }
    } else if (this.state === 'attack' && this.stateT <= 0) {
      this.state = 'chase';
      this.attackCd = this.archetype.attackCooldown;
    }
  }

  /** 'ground' and 'flyer' share this: decide idle vs. chase (vs. kite-retreat for a ranged archetype too close), move accordingly, and open a telegraph once in range with line-of-sight (when required). */
  private updateChaseOrIdle(dt: number, physics: BlockPhysics, nav: NavGrid | null, dirToPlayer: THREE.Vector3, distance: number, separation: THREE.Vector3, playerPos: THREE.Vector3): void {
    let moving = false;
    let retreating = false;

    if (distance > this.archetype.aggroRange) {
      this.state = 'idle';
    } else if (this.archetype.ranged) {
      if (distance > this.archetype.preferredMaxRange) {
        this.state = 'chase';
        moving = true;
      } else if (distance < this.archetype.preferredMinRange) {
        this.state = 'chase';
        moving = true;
        retreating = true;
      } else {
        this.state = 'idle';
      }
    } else if (distance > this.archetype.attackRange) {
      this.state = 'chase';
      moving = true;
    } else {
      this.state = 'idle';
    }

    this.playClip(this.state === 'chase' ? 'run' : 'idle', true);

    if (moving && this.hitStunT <= 0) {
      const targetX = retreating ? this.position.x - dirToPlayer.x * RETREAT_DISTANCE : playerPos.x;
      const targetZ = retreating ? this.position.z - dirToPlayer.z * RETREAT_DISTANCE : playerPos.z;
      if (this.archetype.movementMode === 'ground') {
        // A short local backstep doesn't benefit from a full A* request (the
        // target point is right behind the enemy, well inside open street) —
        // direct-seek only for retreat; real approach uses the nav path.
        if (retreating) this.directGroundSeek(dt, physics, targetX, targetZ, separation);
        else this.moveAlongPath(dt, physics, nav, targetX, targetZ, separation);
      } else {
        this.flyToward(dt, physics, targetX, targetZ, this.homeGroundY + this.archetype.flightHeight, separation);
      }
    } else if (this.archetype.movementMode !== 'ground') {
      // S11 review fix (MEDIUM, spec-fidelity): `wanderRadius > 0` is
      // currently golden_pigeon-only (every pre-S11 archetype has it at 0,
      // ai/archetypes.ts's own comment on the field) — every other flyer's
      // idle behavior is byte-identical to before this change.
      if (this.archetype.wanderRadius > 0) this.wanderIdle(dt, physics, separation);
      else this.hoverInPlace(dt, physics, separation);
    }

    if (this.archetype.movementMode === 'ground') this.snapToGround(physics);

    if (this.attackCd <= 0 && this.hitStunT <= 0 && distance <= this.archetype.attackRange && this.hasLineOfSightIfRequired(physics, playerPos)) {
      this.state = 'telegraph';
      this.stateT = this.archetype.telegraphDuration;
      this.playClip('telegraph1', false);
    }
  }

  private updateLeash(dt: number, physics: BlockPhysics, nav: NavGrid | null, separation: THREE.Vector3): void {
    const dist = Math.hypot(this.homeX - this.position.x, this.homeZ - this.position.z);
    if (dist <= LEASH_ARRIVE_RADIUS) {
      this.state = 'idle';
      this.hasHitThisDive = false;
      return;
    }
    this.playClip('run', true);
    if (this.archetype.movementMode === 'ground') {
      this.moveAlongPath(dt, physics, nav, this.homeX, this.homeZ, separation);
      this.snapToGround(physics);
    } else {
      this.flyToward(dt, physics, this.homeX, this.homeZ, this.homeGroundY + this.archetype.flightHeight, separation);
    }
  }

  /**
   * 'diver' state machine — a genuinely different shape from the shared
   * telegraph/attack resolution above: telegraph is a HOLD (not an in-place
   * lunge), and the "attack" is a committed multi-second dash rather than a
   * single active frame.
   *
   * `dive` flies toward `diveTarget`, a point captured ONCE at the moment
   * telegraph resolves into a dive — not the player's live position every
   * tick. A review caught the original live-tracking version as a real
   * §0.7 fire-time-capture violation: diveSpeed (7.5) exceeds the player's
   * base move speed, so a continuously-retargeting dive is effectively
   * inescapable once telegraphed, contradicting "a retreat during a
   * telegraph should be able to avoid the hit." The hit-test below still
   * reads the LIVE `distance`/`outOfVerticalRange` (a real projectile still
   * has to actually reach whatever's there) — only the flight TARGET is
   * captured, exactly like the ranged lobber's own `targetPos.clone()`.
   * The commit moment also re-checks line-of-sight (a second review
   * finding, shared with updateAttackResolution's own fix): a sightline
   * that broke during the telegraph hold cancels the dive into `recover`
   * instead of committing blind through whatever now blocks it.
   */
  private updateDiver(dt: number, physics: BlockPhysics, playerPos: THREE.Vector3, distance: number, outOfVerticalRange: boolean, separation: THREE.Vector3, result: EnemyTickResult): void {
    if (this.state === 'telegraph') {
      this.stateT -= dt;
      this.position.y = THREE.MathUtils.lerp(this.position.y, this.homeGroundY + this.archetype.flightHeight, Math.min(1, dt * ALTITUDE_LERP_RATE));
      if (this.stateT <= 0) {
        if (outOfVerticalRange || !this.hasLineOfSightIfRequired(physics, playerPos)) {
          this.state = 'recover'; // the ambush fizzles — sightline broke or the target left engagement range during the hold
          this.stateT = this.archetype.recoverDuration;
          return;
        }
        this.state = 'dive';
        this.stateT = this.archetype.diveDuration;
        this.hasHitThisDive = false;
        this.diveTarget.copy(playerPos); // captured ONCE — §0.7 fire-time-capture, not re-derived every dive tick
        this.playClip('attack1', false);
      }
      return;
    }
    if (this.state === 'dive') {
      this.stateT -= dt;
      this.flyToward(dt, physics, this.diveTarget.x, this.diveTarget.z, Math.max(this.diveTarget.y, 0.2), separation, this.archetype.diveSpeed);
      if (!this.hasHitThisDive && !outOfVerticalRange && distance <= this.archetype.attackRange) {
        this.hasHitThisDive = true;
        result.meleeAttackFired = true;
        this.state = 'recover';
        this.stateT = this.archetype.recoverDuration;
      } else if (this.stateT <= 0) {
        this.state = 'recover'; // whiffed — reached the captured point (or ran out of time) without the player actually being there
        this.stateT = this.archetype.recoverDuration;
      }
      return;
    }
    if (this.state === 'recover') {
      this.stateT -= dt;
      this.playClip('idle', true);
      this.flyToward(dt, physics, this.homeX, this.homeZ, this.homeGroundY + this.archetype.flightHeight, separation);
      // Distance-gated, same discipline as updateLeash — recoverDuration is
      // a FLOOR (a minimum vulnerable window), not a guarantee the flight
      // home is actually finished. A dive can carry the diver several
      // meters from home; a pure timer here (the original version) could
      // strand it there once `state='idle'` lands, since idle is
      // deliberately leash-exempt (nothing else would ever pull it back) —
      // a real bug a review caught.
      const distFromHome = Math.hypot(this.position.x - this.homeX, this.position.z - this.homeZ);
      if (this.stateT <= 0 && distFromHome <= LEASH_ARRIVE_RADIUS) {
        this.state = 'idle';
        this.attackCd = this.archetype.attackCooldown;
      }
      return;
    }
    // idle: hover near home, watch for a dive opportunity.
    this.hoverInPlace(dt, physics, separation);
    this.playClip('idle', true);
    if (!outOfVerticalRange && distance <= this.archetype.aggroRange && this.attackCd <= 0 && this.hitStunT <= 0 && this.hasLineOfSightIfRequired(physics, playerPos)) {
      this.state = 'telegraph';
      this.stateT = this.archetype.telegraphDuration;
      this.playClip('telegraph1', false);
    }
  }

  /**
   * C08/Wave M (PLAN-CITY2 §5 M-3) — the sewer den elite's ONE mechanic:
   * "it burrows, then resurfaces at the player's last position" (C08
   * brief). A genuinely different shape from every other movement mode
   * (like 'diver' getting its own `updateDiver` — see this class's own
   * header comment on why three modes already share one class): telegraph
   * -> burrow (goes hidden) -> a submerged hold -> emerge (a single hit
   * check at its own final frame, not a lingering hazard). No chase/idle-
   * wander of its own — it waits at home and strikes via the burrow/emerge
   * cycle, not by pursuing on the surface.
   */
  private updateBurrower(dt: number, playerPos: THREE.Vector3, distance: number, outOfVerticalRange: boolean, result: EnemyTickResult): void {
    switch (this.state) {
      case 'idle':
        // No continuous ground-resnap here (unlike 'ground' mode's own
        // directGroundSeek/applyKnockbackMovement calls) — the burrower
        // never walks; its Y is set once at spawn/respawn and again
        // explicitly on the emerge teleport below, so there's nothing for a
        // per-tick resnap to correct on flat den terrain. See this method's
        // own header comment.
        this.playClip('idle', true);
        if (!outOfVerticalRange && distance <= this.archetype.aggroRange && this.attackCd <= 0 && this.hitStunT <= 0) {
          this.state = 'telegraph';
          this.stateT = this.archetype.telegraphDuration;
          this.playClip('telegraph1', false);
        }
        break;
      case 'telegraph':
        this.stateT -= dt;
        if (this.stateT <= 0) {
          this.state = 'burrow';
          this.stateT = 0;
          this.playClip('special_burrow', false);
        }
        break;
      case 'burrow':
        this.stateT += dt;
        if (this.stateT >= this.clipDurationOr('special_burrow', 0.5)) {
          // §0.7 fire-time-capture — frozen the instant burrow finishes,
          // never re-derived from the player's live position during the
          // submerged hold below.
          this.diveTarget.copy(playerPos);
          this.group.visible = false; // C08 brief: "the game hides the mesh at the final frame"
          result.submergeAt = this.diveTarget.clone();
          this.state = 'submerged';
          this.stateT = 0;
        }
        break;
      case 'submerged':
        this.stateT += dt;
        if (this.stateT >= this.archetype.submergedDuration) {
          this.position.set(this.diveTarget.x, this.homeGroundY, this.diveTarget.z);
          this.group.visible = true;
          this.group.scale.setScalar(this.visual.baseScale);
          this.hasHitThisDive = false;
          this.state = 'emerge';
          this.stateT = 0;
          this.playClip('special_emerge', false);
        }
        break;
      case 'emerge':
        this.stateT += dt;
        if (this.stateT >= this.clipDurationOr('special_emerge', 0.5)) {
          // The hit check happens ONCE, at the clip's end, not per-tick
          // during emerge — "resurfaces at the player's last position": if
          // the player never moved off the captured point, they're caught;
          // if they read the telegraph/ripple and moved away, they aren't.
          // A single instant, not a lingering hazard.
          if (!this.hasHitThisDive && !outOfVerticalRange) {
            const distNow = Math.hypot(playerPos.x - this.position.x, playerPos.z - this.position.z);
            if (distNow <= this.archetype.attackRange) {
              this.hasHitThisDive = true;
              result.meleeAttackFired = true;
            }
          }
          this.state = 'idle';
          this.stateT = 0;
          this.attackCd = this.archetype.attackCooldown;
          this.playClip('idle', true);
        }
        break;
    }
  }

  private clipDurationOr(name: string, fallback: number): number {
    return this.visual.clips?.get(name)?.duration ?? fallback;
  }

  private hasLineOfSightIfRequired(physics: BlockPhysics, playerPos: THREE.Vector3): boolean {
    if (!this.archetype.requiresLineOfSight) return true;
    return physics.segmentClear(this.position.x, this.position.z, playerPos.x, playerPos.z, LOS_CHECK_HEIGHT);
  }

  /** Ground movement toward (targetX,targetZ) via `NavGrid` pathing, re-planned every `REPATH_INTERVAL` (not every tick — A* isn't free) and steered waypoint-by-waypoint. Falls back to a direct seek if no grid was supplied, or a path genuinely couldn't be found (degrades to S02's old behavior rather than freezing the enemy in place). */
  private moveAlongPath(dt: number, physics: BlockPhysics, nav: NavGrid | null, targetX: number, targetZ: number, separation: THREE.Vector3): void {
    let waypoint: NavPoint | null = null;
    if (nav) {
      this.repathTimer -= dt;
      if (this.path.length === 0 || this.repathTimer <= 0) {
        this.repathTimer = REPATH_INTERVAL;
        const found = nav.findPath(physics, this.position.x, this.position.z, targetX, targetZ);
        this.path = found ?? [];
        this.pathIndex = 0;
      }
      while (this.pathIndex < this.path.length - 1 && Math.hypot(this.path[this.pathIndex].x - this.position.x, this.path[this.pathIndex].z - this.position.z) <= PATH_WAYPOINT_ARRIVE_RADIUS) {
        this.pathIndex++;
      }
      waypoint = this.path.length > 0 ? this.path[this.pathIndex] : null;
    }
    const seekX = waypoint ? waypoint.x : targetX;
    const seekZ = waypoint ? waypoint.z : targetZ;
    this.directGroundSeek(dt, physics, seekX, seekZ, separation);
  }

  private directGroundSeek(dt: number, physics: BlockPhysics, targetX: number, targetZ: number, separation: THREE.Vector3): void {
    const dx0 = targetX - this.position.x;
    const dz0 = targetZ - this.position.z;
    const len = Math.hypot(dx0, dz0);
    const dirX = len > 1e-4 ? dx0 / len : 0;
    const dirZ = len > 1e-4 ? dz0 / len : 0;
    const dx = (dirX + separation.x) * this.archetype.moveSpeed * dt;
    const dz = (dirZ + separation.z) * this.archetype.moveSpeed * dt;
    // PLAYER_TOTAL_HEIGHT, not a re-typed 1.4 literal — T1 review note (see
    // layout.ts's STREET_INERT_BOTTOM_Y comment): this enemy vertical band
    // happens to equal the player's own capsule height today, but the two
    // were never actually linked, so a future change to one could silently
    // desync from the other. Behavior is unchanged (still exactly 1.4).
    const resolved = physics.resolveHorizontal(this.position.x, this.position.z, dx, dz, this.archetype.radius, this.position.y, this.position.y + PLAYER_TOTAL_HEIGHT);
    this.position.x = resolved.x;
    this.position.z = resolved.z;
  }

  /** 'flyer'/'diver' horizontal seek toward (targetX,targetZ) at `speedOverride ?? moveSpeed`, plus an altitude ease toward `targetHeight`. Collides with walls (buildings) via a thin vertical band around its OWN current altitude, but never consults a floor region — a flyer never "stands" on anything. */
  private flyToward(dt: number, physics: BlockPhysics, targetX: number, targetZ: number, targetHeight: number, separation: THREE.Vector3, speedOverride?: number): void {
    const dx0 = targetX - this.position.x;
    const dz0 = targetZ - this.position.z;
    const len = Math.hypot(dx0, dz0);
    const dirX = len > 1e-4 ? dx0 / len : 0;
    const dirZ = len > 1e-4 ? dz0 / len : 0;
    const speed = speedOverride ?? this.archetype.moveSpeed;
    const dx = (dirX + separation.x) * speed * dt;
    const dz = (dirZ + separation.z) * speed * dt;
    const resolved = physics.resolveHorizontal(this.position.x, this.position.z, dx, dz, this.archetype.radius, this.position.y - 0.3, this.position.y + 0.3);
    this.position.x = resolved.x;
    this.position.z = resolved.z;
    this.position.y = THREE.MathUtils.lerp(this.position.y, targetHeight, Math.min(1, dt * ALTITUDE_LERP_RATE));
  }

  /** A small in-place wobble + altitude hold — no target-seeking at all, so it's equally correct whether the flyer is idling far from anything or hovering right next to the player waiting out its own attack cooldown. */
  private hoverInPlace(dt: number, physics: BlockPhysics, separation: THREE.Vector3): void {
    this.hoverPhase += dt * FLYER_HOVER_PHASE_SPEED;
    const wobbleX = Math.cos(this.hoverPhase);
    const wobbleZ = Math.sin(this.hoverPhase * 0.7);
    const dx = (wobbleX * FLYER_HOVER_SPEED + separation.x) * dt;
    const dz = (wobbleZ * FLYER_HOVER_SPEED + separation.z) * dt;
    const resolved = physics.resolveHorizontal(this.position.x, this.position.z, dx, dz, this.archetype.radius, this.position.y - 0.3, this.position.y + 0.3);
    this.position.x = resolved.x;
    this.position.z = resolved.z;
    this.position.y = THREE.MathUtils.lerp(this.position.y, this.homeGroundY + this.archetype.flightHeight, Math.min(1, dt * ALTITUDE_LERP_RATE));
  }

  /**
   * S11 review fix: golden_pigeon's real "roamer" behavior — every OTHER
   * flyer's idle state calls `hoverInPlace()` unchanged (this is only
   * reached when `wanderRadius > 0`). Periodically re-picks a random point
   * within `wanderRadius` of HOME (never the player — this archetype has
   * aggroRange 0 and should never react to the player at all) and drifts
   * toward it via the existing `flyToward` primitive at a slow, lazy pace —
   * reusing the same "seek a target point" building block chase/leash
   * already use, rather than a bespoke movement routine. `Math.random()` is
   * correct here per §0.7: this is per-frame ambient juice, not state a
   * restart/replay must reproduce (droptable.ts's own coin-roll comment
   * makes the identical call for the same reason).
   */
  private wanderIdle(dt: number, physics: BlockPhysics, separation: THREE.Vector3): void {
    this.wanderRepickT -= dt;
    const distToTarget = Math.hypot(this.wanderTargetX - this.position.x, this.wanderTargetZ - this.position.z);
    if (this.wanderRepickT <= 0 || distToTarget < WANDER_ARRIVE_RADIUS) {
      this.wanderRepickT = WANDER_REPICK_INTERVAL + Math.random() * WANDER_REPICK_JITTER;
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * this.archetype.wanderRadius;
      this.wanderTargetX = this.homeX + Math.cos(angle) * radius;
      this.wanderTargetZ = this.homeZ + Math.sin(angle) * radius;
    }
    this.flyToward(dt, physics, this.wanderTargetX, this.wanderTargetZ, this.homeGroundY + this.archetype.flightHeight, separation, WANDER_SPEED);
  }

  private applyKnockbackMovement(dt: number, physics: BlockPhysics): void {
    if (this.health.knockbackVel.lengthSq() < 1e-4) return;
    const dx = this.health.knockbackVel.x * dt;
    const dz = this.health.knockbackVel.z * dt;
    const resolved = physics.resolveHorizontal(this.position.x, this.position.z, dx, dz, this.archetype.radius, this.position.y, this.position.y + PLAYER_TOTAL_HEIGHT);
    this.position.x = resolved.x;
    this.position.z = resolved.z;
    if (this.archetype.movementMode === 'ground') this.snapToGround(physics);
  }

  private snapToGround(physics: BlockPhysics): void {
    const ground = physics.groundHeightAt(this.position.x, this.position.z, this.position.y, GROUND_TOLERANCE);
    if (ground !== null) this.position.y = ground;
  }

  private applyPrimitivePulse(): void {
    if (this.visual.mixer) return; // GLB actors get their read from the real telegraph/attack clips instead
    const pulseActive = this.state === 'telegraph' || this.state === 'attack' || this.state === 'dive';
    const target = this.visual.baseScale * (pulseActive ? PRIMITIVE_PULSE_SCALE : 1);
    const current = this.group.scale.x;
    const next = THREE.MathUtils.lerp(current, target, 0.35);
    this.group.scale.setScalar(next);
  }

  private applyFlashTint(enabled: boolean): void {
    // `flashT` (combat/damage.ts) still counts down identically either way —
    // this only gates the VISUAL lerp, never the hit-feedback timing itself
    // (hitstun/knockback/i-frames are unaffected by this setting).
    const lit = enabled && this.health.flashT > 0 ? this.health.flashT / FLASH_DURATION : 0;
    for (const [m, base] of this.baseEmissive) {
      m.emissive.copy(base).lerp(WHITE, lit);
    }
  }

  private syncTransform(): void {
    this.group.position.set(this.position.x, this.position.y, this.position.z);
    // R-1 (PLAN-CITY3 §0 L2): every roster GLB this class instantiates
    // (enm_rat/enm_trash_lobber/enm_pizza_rat/enm_pigeon/enm_divepigeon —
    // re-verified during the cast-wide audit, all +X-forward, no exceptions)
    // is authored on the same convention chr_runner is — see
    // core/assets.ts's CHARACTER_FORWARD_YAW_OFFSET for the full derivation.
    // `this.facing` itself stays the pure movement-direction value (nothing
    // else in this class reads it) — only the cosmetic write gets the offset.
    this.group.rotation.y = this.facing + CHARACTER_FORWARD_YAW_OFFSET;
  }
}
